import React, { useState, useEffect } from "react";
import { Form, Row, Col, Space } from "antd";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./QuizAnswerForm.css";
import {
  appTitle,
  EmployeeFollowUpEventQuizStatus,
  AnsweredBy,
  AnswerType,
  NotApplicableSuffix,
  ErrorSuffix,
} from "../common/GeneralConfigurations";
import {
  getConsultantQuizQuestions,
  consultantQuizAnswers,
} from "../../api/QuizAPI/AnswerAPI";
import {
  getConsultantSatisfactionLevel,
  updateSatisfactionLevel,
} from "../../api/EmployeeFollowUpEventApi";
import AdnMessageSuccess from "../common/adnMessage/AdnMessageSuccess";
import AdnMessageError from "../common/adnMessage/AdnMessageError";
import AdnCard from "../common/adnCard/AdnCard";
import AdnTitle from "../common/adnPageTitle/AdnPageTitle";
import QuizAnswerForm from "./QuizAnswerForm";
import { AppRoutes } from "../../AppRoutes";
import AdnThankYouComponent from "../common/adnThankYouComponent/AdnThankYouComponent";
import AdnSatisfactionRate from "../common/adnSatisfactionLevel/AdnSatisfactionRate";
import AdnSaveButton from "../common/adnSaveButton/AdnSaveButton";

/** Consultant Manager Page */
const ManageQuizAnswerPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [submitLoading] = useState(false);
  const [quizData, setQuizData] = useState();
  const [token, setToken] = useState();
  const [readonly, setReadonly] = useState(true);
  const [quizAnswersValues, setQuizAnswersValues] = useState({});
  const [invalidToken, setInvalidToken] = useState(undefined);
  const [showSatisfactionLevel, setShowSatisfactionLevel] = useState(true);
  const [showError, setShowError] = useState(false);
  const [satisfactionLevel, setSatisfactionLevel] = useState({});
  const [isLanguagePT, setIsLanguagePT] = useState(true);
  const [satisfactionLevelForm] = Form.useForm();

  useEffect(() => {
    document.title = `${appTitle} - ${t("quizAnswer.title")}`;
    if (searchParams.get("token")) {
      let token = searchParams.get("token");
      setToken(token);
      satisfactionLevelForm.resetFields();
      getEmployeeFollowUpEventByToken(token);
    } else {
      navigate(AppRoutes.homeUnauthenticated.path);
    }
  }, [t, searchParams, satisfactionLevelForm, navigate]);

  const getConsultantQuizQuestionsInfo = async (token) => {
    await getConsultantQuizQuestions(token)
      .then((res) => {
        if (res.status === 200) {
          if (res.data) {
            setQuizData(res.data);
            translateQuestionAnswers(res.data);
            // Quiz must be readonly when:
            // - quiz has been answered
            // - it's the supervisor viewing the quiz ant it's in the state AwaitsResponseFromConsultant
            // - it's someone but the supervisor viewing the quiz
            setReadonly(
              res.data.quizStatusId !==
                EmployeeFollowUpEventQuizStatus.AwaitsResponseFromConsultant
            );
            // if the quiz is not in the status «AwaitsResponseFromConsultant» goes to the login page
            if (
              res.data.quizStatusId !==
              EmployeeFollowUpEventQuizStatus.AwaitsResponseFromConsultant
            ) {
              setInvalidToken(true);
            } else {
              setInvalidToken(false);
            }
          } else {
            AdnMessageError(t("quizAnswer.messages.couldNotGetUniqueQuiz"));
          }
        } else if (res.status === 404) {
          // invalid token or quiz not found
          navigate(AppRoutes.homeUnauthenticated.path);
        } else {
          AdnMessageError(t("quizAnswer.messages.couldNotGetQuizInformation"));
        }
      })
      .catch(() => {
        AdnMessageError(t("quizAnswer.messages.couldNotGetQuizInformation"));
      });
  };

  const handleCancel = () => {
    navigate(AppRoutes.homeUnauthenticated.path);
  };

  const handleSaveQuiz = async (values) => {
    const answers = prepareAnswers(values);
    await consultantQuizAnswers(answers)
      .then((res) => {
        if (res.status === 200) {
          AdnMessageSuccess();
          getConsultantQuizQuestionsInfo(searchParams.get("token"));
        } else if (res && res.data && res.data.errors) {
          res.data.errors.map((e) => {
            AdnMessageError(e.message);
            return null;
          });
        } else {
          AdnMessageError(t("quizAnswer.messages.couldNotSaveQuizAnswers"));
        }
      })
      .catch((e) => {
        AdnMessageError(t("quizAnswer.messages.couldNotSaveQuizAnswers"));
      });
  };

  const prepareAnswers = (values) => {
    let answers = {};
    answers.answerId = quizData?.answerId;
    answers.token = token;
    answers.isSubmitted = values.isSubmiting || false;
    answers.isEmployeeAnswer = true;

    const questionAnswers = [];
    quizData &&
      quizData.groups.forEach((grp) => {
        grp.questions.forEach((qst) => {
          // only the supervisor answers the questions
          if (
            qst.answeredBy === AnsweredBy.Consultant ||
            qst.answeredBy === AnsweredBy.Both
          ) {
            let name = `grp_${grp.id}_qst_${qst.id}`;
            let answerValue = null;
            let answerIsNotApplicable = false;
            answerIsNotApplicable = values[name + NotApplicableSuffix] || false;
            if (answerIsNotApplicable === false) {
              answerValue =
                values[name] !== null && values[name] !== undefined
                  ? values[name].toString()
                  : undefined;
            }
            // get QuestionAnswerId
            let questionAnswerId = 0;
            if (
              quizData.questionAnswers &&
              quizData.questionAnswers.length > 0
            ) {
              let questionAnswer = quizData.questionAnswers.find(
                (x) =>
                  x.questionId === qst.id &&
                  x.isEmployeeAnswer === answers.isEmployeeAnswer
              );
              // if it's undefinned it's because only the supervisor has to answer the question (evaluation event)
              if (questionAnswer) {
                questionAnswerId = questionAnswer.id;
              }
            }
            questionAnswers.push({
              id: questionAnswerId,
              answerId: quizData.answerId,
              questionId: qst.id,
              answer: answerValue,
              isNotApplicable: answerIsNotApplicable,
            });
          }
        });
      });
    answers.questionAnswers = questionAnswers;
    return answers;
  };

  const translateQuestionAnswers = (data) => {
    const newQuestionAnswersValues = {};
    data &&
      data.groups.forEach((grp) => {
        grp.questions.forEach((qst) => {
          // only the supervisor answers the questions
          if (
            qst.answeredBy === AnsweredBy.Consultant ||
            qst.answeredBy === AnsweredBy.Both
          ) {
            let name = `grp_${grp.id}_qst_${qst.id}`;
            if (
              data.questionAnswers === null ||
              data.questionAnswers === undefined
            ) {
              newQuestionAnswersValues[name] = null;
              newQuestionAnswersValues[
                name.concat(NotApplicableSuffix)
              ] = false;
              newQuestionAnswersValues[name.concat(ErrorSuffix)] = null;
            } else {
              const qa = data.questionAnswers.find(
                (q) => q.questionId === qst.id
              );
              if (qa === undefined) {
                newQuestionAnswersValues[name] = null;
                newQuestionAnswersValues[
                  name.concat(NotApplicableSuffix)
                ] = false;
                newQuestionAnswersValues[name.concat(ErrorSuffix)] = null;
              } else {
                if (qst.answerTypeId === AnswerType.VerdadeiroFalso) {
                  newQuestionAnswersValues[name] = JSON.parse(qa.answer);
                } else if (
                  qst.answerTypeId === AnswerType.Escala ||
                  qst.answerTypeId === AnswerType.EscolhaUnica
                ) {
                  newQuestionAnswersValues[name] = +qa.answer;
                } else if (qst.answerTypeId === AnswerType.EscolhaMultipla) {
                  if (qa.answer === null || qa.answer === undefined) {
                    newQuestionAnswersValues[name] = undefined;
                  } else {
                    const intArray = qa.answer
                      .split(",")
                      .map((id) => parseInt(id, 10));
                    newQuestionAnswersValues[name] = intArray;
                  }
                } else {
                  newQuestionAnswersValues[name] = qa.answer;
                }
                newQuestionAnswersValues[name.concat(NotApplicableSuffix)] =
                  qa.isNotApplicable;
                newQuestionAnswersValues[name.concat(ErrorSuffix)] = null;
              }
            }
          }
        });
      });
    setQuizAnswersValues(newQuestionAnswersValues);
  };

  const getEmployeeFollowUpEventByToken = async (token) => {
    await getConsultantSatisfactionLevel(token)
      .then((result) => {
        if (result.status === 200) {
          setSatisfactionLevel(result.data);
          setIsLanguagePT(result.data.isLanguagePT);
          if (result.data.eventExists === false) {
            setShowSatisfactionLevel(false);
            setInvalidToken(true);
          } else if (
            result.data.eventExists === true &&
            result.data.contractTypeId === 2
          ) {
            setShowSatisfactionLevel(false);
            getConsultantQuizQuestionsInfo(token);
          } else if (
            result.data.eventExists === true &&
            result.data.companyLevel === null &&
            result.data.clientLevel === null &&
            result.data.projectLevel === null
          ) {
            setShowSatisfactionLevel(true);
          } else {
            setShowSatisfactionLevel(false);
            getConsultantQuizQuestionsInfo(token);
          }
        } else {
          setShowSatisfactionLevel(false);
          setInvalidToken(true);
        }
      })
      .catch((e) => {
        AdnMessageError(t("quizAnswer.messages.couldNotGetSatisfactionLevel"));
      });
  };

  const saveSatisfactionLevel = async (data) => {
    data.token = token;
    await updateSatisfactionLevel(data)
      .then(async (result) => {
        if (result.status === 200) {
          setShowSatisfactionLevel(false);
          await getConsultantQuizQuestionsInfo(searchParams.get("token"));
          AdnMessageSuccess();
          return;
        }
        AdnMessageError(t("quizAnswer.messages.couldNotSaveSatisfactionLevel"));
      })
      .catch((e) => {
        AdnMessageError(t("quizAnswer.messages.couldNotSaveSatisfactionLevel"));
      });
  };

  const handleSatisfactionLevel = async (values) => {
    setSatisfactionLevel(values);
    if (
      values.companyLevel > 0 &&
      values.clientLevel > 0 &&
      values.projectLevel > 0
    ) {
      setShowError(false);
      await saveSatisfactionLevel(values);
    } else {
      setShowError(true);
    }
  };

  return (
    <div className="quiz-answer-page">
      {showSatisfactionLevel && (
        <>
          <AdnTitle title={t("quizAnswer.satisfactionLevel.title")} />
          <AdnCard loading={submitLoading}>
            <Form
              layout="vertical"
              style={{ marginTop: "3rem" }}
              name="satisfactionLevelForm"
              form={satisfactionLevelForm}
              initialValues={satisfactionLevel}
              onFinish={handleSatisfactionLevel}
              autoComplete="off"
            >
              <>
                <Row>
                  {isLanguagePT === true && (
                    <h5>{t("quizAnswer.satisfactionLevel.description")}</h5>
                  )}
                  {isLanguagePT === false && (
                    <h5>{t("quizAnswer.satisfactionLevel.description")}</h5>
                  )}
                </Row>
                <Row gutter={16}>
                  <Col span={24}>
                    <AdnSatisfactionRate
                      companyLevel="companyLevel"
                      clientLevel="clientLevel"
                      projectLevel="projectLevel"
                      style={{ fontSize: 30 }}
                    ></AdnSatisfactionRate>
                  </Col>
                </Row>
                {showError && (
                  <Row justify="start">
                    {isLanguagePT === true && (
                      <p className="error">
                        {t("quizAnswer.satisfactionLevel.errorMessage")}
                      </p>
                    )}
                    {isLanguagePT === false && (
                      <p className="error">
                        {t("quizAnswer.satisfactionLevel.errorMessage")}
                      </p>
                    )}
                  </Row>
                )}
                <Row justify="end">
                  <Space>
                    <Col>
                      <AdnSaveButton loading={submitLoading} />
                    </Col>
                  </Space>
                </Row>
              </>
            </Form>
          </AdnCard>
        </>
      )}

      {!showSatisfactionLevel && (
        <>
          {invalidToken === true && (
            <AdnThankYouComponent
              IsPTLanguage={quizData?.quizLanguage === "PT"}
            />
          )}
          {invalidToken === false && (
            <>
              <AdnTitle title={t("quizAnswer.title")} />
              <AdnCard loading={submitLoading}>
                <QuizAnswerForm
                  iAmSupervisor={false}
                  readonly={readonly}
                  eventTypeName={quizData?.eventName}
                  consultantName={quizData?.answeredByName}
                  supervisorName={quizData?.supervisedByName}
                  quizStatusId={quizData?.quizStatusId}
                  quizStatusDescription={quizData?.quizStatusDescription}
                  submitLoading={submitLoading}
                  handleCancel={handleCancel}
                  handleSaveQuiz={handleSaveQuiz}
                  quizData={quizData}
                  quizAnswersValues={quizAnswersValues}
                  invalidToken={invalidToken}
                />
              </AdnCard>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ManageQuizAnswerPage;
